import { KIND } from './kinds.js';
import { signEvent } from './event.js';
import { tagValue } from './util.js';

/**
 * A channel is the unit of consent. A subscriber never consents to "a company";
 * they consent to one channel, whose declared terms (type, frequency cap,
 * retention) are part of what they signed. Changing the terms requires
 * publishing a new channel definition, which the client can detect and re-ask.
 */

export const CHANNEL_TYPES = ['newsletter', 'transactional', 'alerts'];
export const PRIVACY_MODES = ['anonymous_ok', 'identified'];

const WINDOW_UNITS = { h: 3600, d: 86400, w: 604800 };

/**
 * Parse a frequency cap like "4/30d" -> { count: 4, windowSeconds: 2592000 }.
 * The cap is a promise the sender makes at subscribe time and that both the
 * relay and the client enforce afterwards.
 */
export function parseCap(cap) {
  if (cap === undefined || cap === null || cap === '' || cap === 'unlimited') return null;
  const m = /^(\d+)\/(\d+)([hdw])$/.exec(String(cap).trim());
  if (!m) throw new Error(`invalid frequency cap "${cap}" (expected e.g. "4/30d")`);
  const count = Number(m[1]);
  if (count < 1) throw new Error('frequency cap count must be >= 1');
  return { count, windowSeconds: Number(m[2]) * WINDOW_UNITS[m[3]], raw: String(cap).trim() };
}

export function formatCap(cap) {
  const parsed = parseCap(cap);
  if (!parsed) return 'no declared limit';
  const days = parsed.windowSeconds / 86400;
  const window = days >= 1 ? `${days} day${days === 1 ? '' : 's'}` : `${parsed.windowSeconds / 3600} hours`;
  return `max ${parsed.count} message${parsed.count === 1 ? '' : 's'} / ${window}`;
}

/**
 * Build a signed channel definition.
 * @param {{slug:string, name:string, description?:string, type?:string,
 *          cap?:string, retentionDays?:number, privacy?:string, locale?:string}} def
 */
export function buildChannel(def, secretKey) {
  if (!/^[a-z0-9][a-z0-9._-]{0,63}$/.test(def.slug ?? '')) {
    throw new Error('channel slug must be lowercase alphanumeric with . _ - (max 64 chars)');
  }
  const type = def.type ?? 'newsletter';
  if (!CHANNEL_TYPES.includes(type)) throw new Error(`channel type must be one of ${CHANNEL_TYPES.join(', ')}`);
  const privacy = def.privacy ?? (type === 'newsletter' ? 'anonymous_ok' : 'identified');
  if (!PRIVACY_MODES.includes(privacy)) throw new Error(`privacy must be one of ${PRIVACY_MODES.join(', ')}`);
  if (type !== 'newsletter' && privacy === 'anonymous_ok') {
    throw new Error(`channel type "${type}" targets individuals and therefore requires privacy "identified"`);
  }
  const cap = parseCap(def.cap ?? (type === 'newsletter' ? '4/30d' : null));

  const content = JSON.stringify({
    name: def.name,
    description: def.description ?? '',
    type,
    privacy,
    cap: cap?.raw ?? null,
    retention_days: def.retentionDays ?? 90,
    locale: def.locale ?? 'en',
  });

  const tags = [
    ['d', def.slug],
    ['name', def.name],
    ['type', type],
    ['privacy', privacy],
  ];
  if (cap) tags.push(['cap', cap.raw]);

  return signEvent({ kind: KIND.CHANNEL, content, tags, created_at: def.createdAt }, secretKey);
}

/** Parse a channel definition event into a plain object. Throws on malformed content. */
export function parseChannel(event) {
  if (event.kind !== KIND.CHANNEL) throw new Error(`not a channel definition (kind ${event.kind})`);
  const slug = tagValue(event, 'd');
  if (!slug) throw new Error('channel definition missing d tag');
  let body;
  try {
    body = JSON.parse(event.content);
  } catch {
    throw new Error('channel content is not valid JSON');
  }
  return {
    slug,
    sender: event.pubkey,
    name: body.name ?? slug,
    description: body.description ?? '',
    type: body.type ?? 'newsletter',
    privacy: body.privacy ?? 'identified',
    cap: body.cap ?? null,
    retentionDays: body.retention_days ?? 90,
    locale: body.locale ?? 'en',
    updatedAt: event.created_at,
  };
}

/**
 * The exact string a subscriber signs as "the terms I accepted". Any later
 * change by the sender produces a different digest, so a client can prove it
 * never agreed to the new terms.
 */
export function termsDigestInput(channel) {
  return JSON.stringify({
    sender: channel.sender,
    slug: channel.slug,
    type: channel.type,
    privacy: channel.privacy,
    cap: channel.cap ?? null,
    retention_days: channel.retentionDays,
  });
}
